/**
 * Cliente RPC — Portal Cliente Menuee
 *
 * Responsabilidad: invocar RPC SECURITY DEFINER con envelope estándar
 * { ok, code, data, idempotent, message } e idempotencia unificada.
 *
 * Consume: supabaseClient, utils/idempotency (generateIdempotencyKey).
 *
 * Qué nunca debe hacer:
 * - INSERT directo en tablas operativas o financieras.
 * - Recalcular montos de pedido, delivery o cobro.
 * - Invocar RPC de Admin exclusivas (E0–E5, E2, E3, liquidación).
 * - Contener lógica visual ni JSX.
 *
 * Referencia: MENUEE_BACKEND_CONTRACT.md — Cliente RPC (frontend).
 */

import { supabase } from "../supabaseClient";
import { generateIdempotencyKey as createIdempotencyKey } from "../utils/idempotency";

/**
 * Error tipado para fallos de RPC Menuee.
 */
export class RpcClientError extends Error {
  /**
   * @param {string} message
   * @param {Object} [meta]
   * @param {string} [meta.code]
   * @param {string} [meta.rpcName]
   * @param {Object} [meta.envelope]
   * @param {import('@supabase/supabase-js').PostgrestError} [meta.cause]
   */
  constructor(message, meta = {}) {
    super(message);
    this.name = "RpcClientError";
    this.code = meta.code ?? null;
    this.rpcName = meta.rpcName ?? null;
    this.envelope = meta.envelope ?? null;
    this.cause = meta.cause ?? null;
  }
}

/**
 * Genera UUID para p_idempotency_key. Delega en utils/idempotency.
 * Reexportado aquí por contrato Menuee (MENUEE_BACKEND_CONTRACT.md).
 *
 * @returns {string}
 */
export function generateIdempotencyKey() {
  return createIdempotencyKey();
}

/**
 * Extrae data de un envelope exitoso. Lanza RpcClientError si ok !== true.
 *
 * @param {Object} envelope
 * @returns {*}
 */
export function unwrapRpcData(envelope) {
  if (!envelope || envelope.ok !== true) {
    throw mapRpcError(envelope);
  }
  return envelope.data;
}

/**
 * Convierte envelope de error o error PostgREST a RpcClientError.
 *
 * @param {Object|import('@supabase/supabase-js').PostgrestError|null|undefined} source
 * @param {string} [rpcName]
 * @returns {RpcClientError}
 */
export function mapRpcError(source, rpcName) {
  if (!source) {
    return new RpcClientError("Error RPC desconocido", { rpcName });
  }

  if (source instanceof RpcClientError) {
    return source;
  }

  // Error de transporte PostgREST (red, RLS, función inexistente).
  if (source.message && source.code && source.details !== undefined) {
    return new RpcClientError(source.message, {
      code: source.code,
      rpcName,
      cause: source,
    });
  }

  // Envelope Menuee con ok: false.
  const message =
    source.message ||
    source.code ||
    "RPC falló";

  return new RpcClientError(message, {
    code: source.code ?? null,
    rpcName,
    envelope: source,
  });
}

/**
 * @typedef {Object} CallRpcOptions
 * @property {string} [idempotencyKey] - UUID; si se omite y hay idempotencyParam, se genera uno.
 * @property {string} [idempotencyParam='p_idempotency_key'] - Nombre del parámetro en el RPC.
 */

/**
 * Invoca un RPC de Menuee y devuelve el envelope completo.
 *
 * @param {string} rpcName - Nombre de la función (ej. 'rpc_crear_pedido').
 * @param {Object} [params={}] - Parámetros del RPC.
 * @param {CallRpcOptions} [options={}]
 * @returns {Promise<{ ok: boolean, code: string, data: *, idempotent: boolean, message: string }>}
 */
export async function callRpc(rpcName, params = {}, options = {}) {
  const { idempotencyParam = "p_idempotency_key" } = options;
  let { idempotencyKey } = options;

  const rpcParams = { ...params };

  if (idempotencyParam) {
    if (!idempotencyKey) {
      idempotencyKey = generateIdempotencyKey();
    }
    rpcParams[idempotencyParam] = idempotencyKey;
  }

  const { data, error } = await supabase.rpc(rpcName, rpcParams);

  if (error) {
    throw mapRpcError(error, rpcName);
  }

  if (data && typeof data === "object" && "ok" in data) {
    if (data.ok !== true) {
      throw mapRpcError(data, rpcName);
    }
    return data;
  }

  // RPC sin envelope (no esperado en Menuee operativo); normalizar para unwrapRpcData.
  return {
    ok: true,
    code: null,
    data,
    idempotent: false,
    message: null,
  };
}
